import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Tag,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FacebookFilled,
  GoogleOutlined,
} from '@ant-design/icons';
import { pixelService, PixelConfig } from '../services/pixel';

const { Title } = Typography;

const PixelConfigPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('facebook');
  const [configs, setConfigs] = useState<{ facebook?: PixelConfig[]; google?: PixelConfig[] }>({});
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<PixelConfig | null>(null);
  const [form] = Form.useForm();

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const data = await pixelService.getPixelConfigs();
      setConfigs(data);
    } catch (error) {
      message.error('获取像素配置失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleAdd = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({ platform: activeTab });
    setModalVisible(true);
  };

  const handleEdit = (record: PixelConfig) => {
    setEditingConfig(record);
    form.setFieldsValue({
      pixel_code: record.pixel_code,
      pixel_name: record.pixel_name,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await pixelService.deletePixelConfig(id);
      message.success('删除成功');
      fetchConfigs();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingConfig) {
        await pixelService.updatePixelConfig({
          id: editingConfig.id,
          ...values,
        });
        message.success('更新成功');
      } else {
        await pixelService.createPixelConfig({
          platform: activeTab,
          ...values,
        });
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchConfigs();
    } catch (error: any) {
      message.error(error.message || '操作失败');
    }
  };

  const getFacebookColumns = () => [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '像素ID', dataIndex: 'pixel_code', width: 200 },
    { title: '名称', dataIndex: 'pixel_name', width: 150 },
    { title: 'Access Token', dataIndex: 'access_token', ellipsis: true, render: (text: string) => text || '-' },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (status: number) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作', width: 150,
      render: (_: any, record: PixelConfig) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除此像素配置？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getGoogleColumns = () => [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '数据流名称', dataIndex: 'pixel_name', width: 150 },
    { title: '数据流ID', dataIndex: 'pixel_code', width: 120 },
    { title: '衡量ID', dataIndex: 'access_token', width: 140, render: (text: string) => text || '-' },
    { title: 'API密钥', dataIndex: 'api_secret', ellipsis: true, render: (text: string) => text || '-' },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (status: number) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作', width: 120,
      render: (_: any, record: PixelConfig) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除此像素配置？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'facebook',
      label: (
        <span>
          <FacebookFilled style={{ color: '#1877f2' }} /> Facebook
        </span>
      ),
      children: (
        <Table
          columns={getFacebookColumns()}
          dataSource={configs.facebook || []}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      ),
    },
    {
      key: 'google',
      label: (
        <span>
          <GoogleOutlined style={{ color: '#4285f4' }} /> Google
        </span>
      ),
      children: (
        <Table
          columns={getGoogleColumns()}
          dataSource={configs.google || []}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>像素配置</Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            添加{activeTab === 'facebook' ? 'Facebook' : 'Google'}像素
          </Button>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      <Modal
        title={editingConfig ? '编辑像素配置' : '添加像素配置'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={500}
      >
        <Form form={form} layout="vertical">
          {activeTab === 'facebook' && (
            <>
              <Form.Item
                name="pixel_code"
                label="像素ID (Pixel ID)"
                rules={[{ required: true, message: '请输入像素ID' }]}
              >
                <Input placeholder="如: 123456789012345" />
              </Form.Item>
              <Form.Item
                name="pixel_name"
                label="像素名称"
              >
                <Input placeholder="自定义名称，便于识别" />
              </Form.Item>
              <Form.Item
                name="access_token"
                label="Access Token"
                rules={[{ required: true, message: '请输入 Access Token' }]}
                extra="从 Facebook 事件管理器 > 数据源 > 设置 > 转化API 获取"
              >
                <Input.TextArea rows={3} placeholder="EAAxxxxxxx..." />
              </Form.Item>
            </>
          )}
          {activeTab === 'google' && (
            <>
              <Form.Item
                name="pixel_name"
                label="数据流名称"
                rules={[{ required: true, message: '请输入数据流名称' }]}
              >
                <Input placeholder="如: example.com" />
              </Form.Item>
              <Form.Item
                name="pixel_code"
                label="数据流ID"
                rules={[{ required: true, message: '请输入数据流ID' }]}
              >
                <Input placeholder="如: 13302211718" />
              </Form.Item>
              <Form.Item
                name="access_token"
                label="衡量ID (Measurement ID)"
                rules={[{ required: true, message: '请输入衡量ID' }]}
                extra="格式: G-XXXXXXXXXX"
              >
                <Input placeholder="如: G-7CZS76YPKT" />
              </Form.Item>
              <Form.Item
                name="api_secret"
                label="Measurement Protocol API密钥"
                rules={[{ required: true, message: '请输入 API 密钥' }]}
                extra="从 Google Analytics > 管理 > 数据流 > Measurement Protocol API Secret 获取"
              >
                <Input placeholder="如: EI_JsdpvQrKvF6P01jtDNA" />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default PixelConfigPage;
